import { datadogRum } from '@datadog/browser-rum';
import type { RumInitConfiguration } from '@datadog/browser-rum';
import { scrubRumEvent } from './rum-scrub.util';

/** One RUM service for every environment; the environment goes in `env`. */
const RUM_SERVICE = 'ia-eva';

/**
 * Builds the Datadog RUM init configuration. Pure (no side effects) so it can be
 * tested; `main.ts` only calls `datadogRum.init` with the result.
 *
 * @param runtime `/assets/config.json` (written by the Release), or null if unavailable
 * @param env     build-time `envConfig`
 * @returns null when credentials are missing (RUM stays off)
 */
export function buildRumInitConfiguration(
  runtime: Record<string, string> | null,
  env: Record<string, string>,
): RumInitConfiguration | null {
  const src = runtime?.['VITE_DD_APPLICATION_ID'] ? runtime : env;
  if (!src['VITE_DD_APPLICATION_ID'] || !src['VITE_DD_CLIENT_TOKEN']) return null;

  const apiBase = (runtime?.['VITE_API_BASE_URL'] || env['VITE_API_BASE_URL'] || '').replace(/\/$/, '');
  const tracingOn = (runtime?.['VITE_DD_TRACING_ENABLED'] ?? env['VITE_DD_TRACING_ENABLED']) === 'true';
  // Only our API: never S3 presigned URLs, Cognito, config.json or localhost.
  // `propagateTraceBaggage` stays unset: the `baggage` header would carry `user.id` (an email).
  const tracing = tracingOn && apiBase.startsWith('https://')
    ? {
        allowedTracingUrls: [{ match: `${apiBase}/api/v2/`, propagatorTypes: ['datadog' as const, 'tracecontext' as const] }],
        traceSampleRate: 100,
      }
    : { allowedTracingUrls: [] };

  return {
    applicationId: src['VITE_DD_APPLICATION_ID'],
    clientToken: src['VITE_DD_CLIENT_TOKEN'],
    site: src['VITE_DD_SITE'] || 'datadoghq.com',
    service: RUM_SERVICE,
    env: src['VITE_DD_ENV'] || 'dev',
    version: env['BUILD_VERSION'] || src['VITE_DD_VERSION'] || 'unknown', // the build wins over config.json
    sessionSampleRate: Number(src['VITE_DD_SESSION_SAMPLE_RATE'] || 100),
    sessionReplaySampleRate: Number(src['VITE_DD_SESSION_REPLAY_SAMPLE_RATE'] || 100),
    defaultPrivacyLevel: 'mask-user-input',
    // Without this the SDK uses the visible text as the click action name (statements, options, emails).
    enablePrivacyForActionName: true,
    trackBfcacheViews: true,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    beforeSend: (event) => scrubRumEvent(event, apiBase),
    ...tracing,
  };
}

/** Reports a failed Angular bootstrap; `addError` is buffered by the SDK until `init`. */
export function reportBootstrapError(err: unknown): void {
  console.warn('[BOOTSTRAP] failed', err);
  datadogRum.addError(err instanceof Error ? err : new Error(String(err)), { phase: 'bootstrap' });
}
